import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { emailsAPI } from '../../services/api';

// Async thunks for email operations
export const getEmailTemplates = createAsyncThunk(
  'emails/getEmailTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await emailsAPI.getTemplates();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch email templates');
    }
  }
);

export const getEmailTemplateById = createAsyncThunk(
  'emails/getEmailTemplateById',
  async (templateId, { rejectWithValue }) => {
    try {
      const response = await emailsAPI.getTemplateById(templateId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch email template');
    }
  }
);

export const updateEmailTemplate = createAsyncThunk(
  'emails/updateEmailTemplate',
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await emailsAPI.updateTemplate(templateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update email template');
    }
  }
);

export const testEmailTemplate = createAsyncThunk(
  'emails/testEmailTemplate',
  async ({ templateId, email }, { rejectWithValue }) => {
    try {
      const response = await emailsAPI.sendTestEmail(templateId, email);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send test email');
    }
  }
);

// Initial state
const initialState = {
  templates: [],
  currentTemplate: null,
  loading: false,
  testEmailSending: false,
  error: null,
  success: null,
};

// Email slice
const emailsSlice = createSlice({
  name: 'emails',
  initialState,
  reducers: {
    clearEmailError: (state) => {
      state.error = null;
    },
    clearEmailSuccess: (state) => {
      state.success = null;
    },
    setCurrentTemplate: (state, action) => {
      state.currentTemplate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all templates
      .addCase(getEmailTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEmailTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(getEmailTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch email templates';
      })
      
      // Get template by ID
      .addCase(getEmailTemplateById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEmailTemplateById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTemplate = action.payload;
      })
      .addCase(getEmailTemplateById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch email template';
      })
      
      // Update template
      .addCase(updateEmailTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateEmailTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Email template updated successfully';
        
        // Update the template in the templates array
        const index = state.templates.findIndex(template => template.id === action.payload.id);
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
        
        // Update current template if it's the one being edited
        if (state.currentTemplate && state.currentTemplate.id === action.payload.id) {
          state.currentTemplate = action.payload;
        }
      })
      .addCase(updateEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update email template';
      })
      
      // Test email
      .addCase(testEmailTemplate.pending, (state) => {
        state.testEmailSending = true;
        state.error = null;
      })
      .addCase(testEmailTemplate.fulfilled, (state) => {
        state.testEmailSending = false;
        state.success = 'Test email sent successfully';
      })
      .addCase(testEmailTemplate.rejected, (state, action) => {
        state.testEmailSending = false;
        state.error = action.payload || 'Failed to send test email';
      });
  },
});

export const { clearEmailError, clearEmailSuccess, setCurrentTemplate } = emailsSlice.actions;

export default emailsSlice.reducer;